import { AppLayout } from '@/components/layouts/AppLayout';
import { PublicLayout } from '@/components/layouts/PublicLayout';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from '@/hooks/useAuth';
import { usePageviewTracking } from '@/hooks/usePageviewTracking';
import { queryClient } from '@/lib/query-client';
import ApiKeys from '@/pages/ApiKeys';
import Dashboard from '@/pages/Dashboard';
import Datasets from '@/pages/Datasets';
import Discoveries from '@/pages/Discoveries';
import DiscoveryDetails from '@/pages/DiscoveryDetails';
import DiscoveryNew from '@/pages/DiscoveryNew';
import ForgotPassword from '@/pages/ForgotPassword';
import Knowledge from '@/pages/Knowledge';
import KnowledgeNew from '@/pages/KnowledgeNew';
import NotFound from '@/pages/NotFound';
import ResetPassword from '@/pages/ResetPassword';
import Settings from '@/pages/Settings';
import SignIn from '@/pages/SignIn';
import SignUp from '@/pages/SignUp';
import Support from '@/pages/Support';
import SupportTicket from '@/pages/SupportTicket';
import VerifyEmail from '@/pages/VerifyEmail';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

// Code-split /billing: pulls in `@paddle/paddle-js` (which then runtime-loads
// the Paddle SDK from cdn.paddle.com). Keeps the main bundle slim for users
// who never visit billing.
const Billing = lazy(() => import('@/pages/Billing'));

// Public vertical landing — lazy so anonymous visitors hitting /sign-in
// don't pay for the marketing copy + Accordion/Table chunks.
const MarketingAttribution = lazy(() => import('@/pages/MarketingAttribution'));

// Public comparison page at `/vs` — DAGsight vs causal-learn vs DoWhy.
// Lazy so the auth/dashboard bundles don't carry the marketing Table chunk.
const Compare = lazy(() => import('@/pages/Compare'));
// Public API landing at `/api` — SEO target "causal discovery API".
const ApiDocs = lazy(() => import('@/pages/ApiDocs'));

// Public blog — distribution/SEO surface. Lazy so the auth/dashboard bundles
// don't carry react-markdown + the article bodies.
const Blog = lazy(() => import('@/pages/Blog'));
const BlogPost = lazy(() => import('@/pages/BlogPost'));

// Public homepage at `/` — primary conversion surface for cold traffic.
// Lazy so authenticated users hitting /dashboard don't pay for it.
const Home = lazy(() => import('@/pages/Home'));

// Public read-only graph viewer (no JWT). Lazy because it pulls in the
// GraphView + xyflow + elkjs chunks; signed-in users on other pages don't
// need to pay for it.
const SharedDiscovery = lazy(() => import('@/pages/SharedDiscovery'));

// Founder-only week-1 launch dashboard. Lazy — pulls in recharts and is
// touched ~once/day by one user (the founder).
const AdminWeek1 = lazy(() => import('@/pages/AdminWeek1'));

// Superuser-only business overview. Lazy — pulls in recharts; never loaded
// for the 99% of users who aren't admins.
const AdminOverview = lazy(() => import('@/pages/AdminOverview'));

// Superuser-only user management + impersonation. Lazy for the same reason.
const AdminUsers = lazy(() => import('@/pages/AdminUsers'));

// Superuser-only support ticket triage. Lazy — only admins ever load it.
const AdminTickets = lazy(() => import('@/pages/AdminTickets'));
const AdminTicket = lazy(() => import('@/pages/AdminTicket'));

// Superuser-only first-party traffic analytics. Lazy — pulls in recharts.
const AdminTraffic = lazy(() => import('@/pages/AdminTraffic'));

// Mounted once just inside the Router so usePageviewTracking can subscribe to
// location changes. Renders nothing — it only fires fire-and-forget telemetry.
function RouteTracker() {
  usePageviewTracking();
  return null;
}

function FullPageLoader() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <FullPageLoader />;
  if (!isAuthenticated) return <Navigate to="/sign-in" replace state={{ from: location }} />;
  return <Outlet />;
}

/**
 * Client-side gate for /admin/*. The server enforces is_superuser on every
 * admin endpoint (defense-in-depth); this just stops a non-admin who types
 * the URL from seeing the page chrome before the API 403s. Nested inside
 * ProtectedRoute, so by here the user is loaded and authenticated.
 */
function AdminGuard() {
  const { isSuperuser } = useAuth();
  if (!isSuperuser) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/**
 * Index route behavior:
 *  - Authenticated users → /dashboard (their working surface)
 *  - Anonymous visitors → public Home landing (the conversion surface)
 *
 * Critical UX decision: cold traffic from LinkedIn / HN / cold outreach
 * lands on `/` and MUST see the value proposition before being asked to
 * sign up. Earlier IndexRedirect → /sign-in was the worst single conversion
 * leak in the funnel — fixed 2026-05-22.
 */
function IndexRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullPageLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Home />
    </Suspense>
  );
}

function FallbackError({ error }: { error: unknown }) {
  return (
    <div className="flex h-full min-h-screen flex-col items-center justify-center gap-2 p-6 text-center">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ error }) => <FallbackError error={error} />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <RouteTracker />
          <Routes>
            <Route index element={<IndexRoute />} />

            {/* Public marketing routes — accessible without auth, no PublicLayout
                redirect side-effects. Lazy-loaded so they don't bloat the auth bundle. */}
            <Route
              path="/marketing-attribution"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <MarketingAttribution />
                </Suspense>
              }
            />

            <Route
              path="/vs"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <Compare />
                </Suspense>
              }
            />

            <Route
              path="/causal-discovery-api"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ApiDocs />
                </Suspense>
              }
            />

            <Route
              path="/blog"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <Blog />
                </Suspense>
              }
            />

            <Route
              path="/blog/:slug"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <BlogPost />
                </Suspense>
              }
            />

            {/* Public read-only share — anonymous, no PublicLayout chrome. */}
            <Route
              path="/share/:token"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <SharedDiscovery />
                </Suspense>
              }
            />

            <Route element={<PublicLayout />}>
              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/sign-up" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/discoveries" element={<Discoveries />} />
                <Route path="/discoveries/new" element={<DiscoveryNew />} />
                <Route path="/discoveries/:id" element={<DiscoveryDetails />} />
                <Route path="/datasets" element={<Datasets />} />
                <Route path="/knowledge" element={<Knowledge />} />
                <Route path="/knowledge/new" element={<KnowledgeNew />} />
                <Route path="/knowledge/:id/edit" element={<KnowledgeNew />} />
                <Route path="/api-keys" element={<ApiKeys />} />
                <Route path="/support" element={<Support />} />
                <Route path="/support/tickets/:id" element={<SupportTicket />} />
                <Route
                  path="/billing"
                  element={
                    <Suspense fallback={<FullPageLoader />}>
                      <Billing />
                    </Suspense>
                  }
                />

                <Route path="/settings" element={<Settings />} />

                {/* Superuser-only admin surface. AdminGuard redirects
                    non-superusers client-side; every /v1/admin/* endpoint
                    independently enforces is_superuser server-side. */}
                <Route element={<AdminGuard />}>
                  <Route
                    path="/admin/overview"
                    element={
                      <Suspense fallback={<FullPageLoader />}>
                        <AdminOverview />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <Suspense fallback={<FullPageLoader />}>
                        <AdminUsers />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/admin/tickets"
                    element={
                      <Suspense fallback={<FullPageLoader />}>
                        <AdminTickets />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/admin/tickets/:id"
                    element={
                      <Suspense fallback={<FullPageLoader />}>
                        <AdminTicket />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/admin/traffic"
                    element={
                      <Suspense fallback={<FullPageLoader />}>
                        <AdminTraffic />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/admin/week1"
                    element={
                      <Suspense fallback={<FullPageLoader />}>
                        <AdminWeek1 />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
